import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, DatePicker, Form, Input, InputNumber, Row, Select, Space, Table, Tag, message, Popconfirm, Statistic } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { AuthService } from '../services/authService';
import { teacherExtraHoursService, TeacherExtraHourItem } from '../services/teacherExtraHoursService';

const buoiOptions = ['Sáng', 'Chiều', 'Tối'];
const categoryOptions = ['Coi thi', 'Chấm thi', 'Lâm sàng'];

type FormValues = {
  Ngay?: Dayjs;
  Buoi?: string;
  Category?: string;
  KhoaLopNhom?: string;
  NoiDung?: string;
  SoPhut?: number;
  SoBai?: number;
  SoBuoi?: number;
};

const columnsDef = (
  onEdit: (record: any) => void,
  onDelete: (id: number) => void,
): any[] => [
  { title: 'Ngày', dataIndex: 'Ngay', key: 'Ngay', render: (v: string) => dayjs(v).format('DD/MM/YYYY') },
  { title: 'Buổi', dataIndex: 'Buoi', key: 'Buoi' },
  { title: 'Nội dung', dataIndex: 'NoiDung', key: 'NoiDung' },
  { title: 'Khóa/Lớp/Nhóm', dataIndex: 'KhoaLopNhom', key: 'KhoaLopNhom' },
  { title: 'Số phút', dataIndex: 'SoPhut', key: 'SoPhut', align: 'right' },
  { title: 'Số bài', dataIndex: 'SoBai', key: 'SoBai', align: 'right' },
  { title: 'Số buổi', dataIndex: 'SoBuoi', key: 'SoBuoi', align: 'right' },
  { title: 'Loại', dataIndex: 'Category', key: 'Category', render: (v: string) => v ? <Tag color="blue">{v}</Tag> : null },
  {
    title: 'Thao tác', key: 'actions', render: (_: any, record: any) => (
      <Space>
        <Button size="small" onClick={() => onEdit(record)}>Sửa</Button>
        <Popconfirm title="Xóa mục này?" onConfirm={() => onDelete(record.ID)}>
          <Button danger size="small">Xóa</Button>
        </Popconfirm>
      </Space>
    )
  }
];

const TeacherExtraHoursPage: React.FC = () => {
  const [form] = Form.useForm<FormValues>();
  const [items, setItems] = useState<(TeacherExtraHourItem & { ID: number; TeacherId: string })[]>([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(dayjs().startOf('month'));
  const [toDate, setToDate] = useState(dayjs());
  const [editId, setEditId] = useState<number | null>(null);

  const totals = useMemo(() => {
    return {
      phut: items.reduce((s, x) => s + (x.SoPhut || 0), 0),
      bai: items.reduce((s, x) => s + (x.SoBai || 0), 0),
      buoi: items.reduce((s, x) => s + (x.SoBuoi || 0), 0),
    };
  }, [items]);

  const load = async () => {
    try {
      setLoading(true);
      const data = await teacherExtraHoursService.list({
        fromDate: fromDate.format('YYYY-MM-DD'),
        toDate: toDate.format('YYYY-MM-DD'),
      });
      setItems(data);
    } catch (e: any) {
      message.error(e?.message || 'Lỗi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload: Partial<TeacherExtraHourItem> = {
        Ngay: values.Ngay ? (values.Ngay as Dayjs).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        Buoi: values.Buoi,
        NoiDung: values.NoiDung,
        KhoaLopNhom: values.KhoaLopNhom,
        SoPhut: values.SoPhut || 0,
        SoBai: values.SoBai || 0,
        SoBuoi: values.SoBuoi || 0,
        Category: values.Category,
      };
      const res = await teacherExtraHoursService.save(editId ? { ID: editId, ...payload } : payload);
      if (res.success) {
        message.success(editId ? 'Đã cập nhật' : 'Đã lưu');
        form.resetFields();
        setEditId(null);
        await load();
      } else {
        message.error(res.message || 'Lưu thất bại');
      }
    } catch (e: any) {
      if (e?.errorFields) return; // validation errors
      message.error(e?.message || 'Lỗi lưu');
    }
  };

  const onDelete = async (id: number) => {
    try {
      await teacherExtraHoursService.remove(id);
      message.success('Đã xóa');
      await load();
    } catch (e: any) {
      message.error(e?.message || 'Lỗi xóa');
    }
  };

  const onEdit = (record: any) => {
    setEditId(record.ID);
    form.setFieldsValue({
      Ngay: record.Ngay ? dayjs(record.Ngay) : undefined,
      Buoi: record.Buoi,
      Category: record.Category,
      KhoaLopNhom: record.KhoaLopNhom,
      NoiDung: record.NoiDung,
      SoPhut: record.SoPhut ?? 0,
      SoBai: record.SoBai ?? 0,
      SoBuoi: record.SoBuoi ?? 0,
    });
  };

  const onCancelEdit = () => {
    setEditId(null);
    form.resetFields();
  };

  const printMonthSheet = () => {
    const m = fromDate.month();
    const y = fromDate.year();
    const monthItems = items
      .filter(it => {
        const d = dayjs(it.Ngay);
        return d.month() === m && d.year() === y;
      })
      .sort((a, b) => dayjs(a.Ngay).unix() - dayjs(b.Ngay).unix());

    if (!monthItems.length) {
      message.warning(`Không có dữ liệu trong tháng ${m + 1}/${y}`);
      return;
    }

    const totalPhut = monthItems.reduce((s, x) => s + (x.SoPhut || 0), 0);
    const totalBai = monthItems.reduce((s, x) => s + (x.SoBai || 0), 0);
    const totalBuoi = monthItems.reduce((s, x) => s + (x.SoBuoi || 0), 0);

    const user = AuthService.getUser?.() || JSON.parse(localStorage.getItem('quanly_truonghoc_user') || '{}');
    const teacherName = user?.FullName || user?.Username || '';

    const rowsHtml = monthItems.map(row => `
      <tr>
        <td>${dayjs(row.Ngay).format('DD/MM/YYYY')}</td>
        <td>${row.Buoi || ''}</td>
        <td>${row.NoiDung || ''}</td>
        <td>${row.KhoaLopNhom || ''}</td>
        <td class="num">${row.SoPhut ?? 0}</td>
        <td class="num">${row.SoBai ?? 0}</td>
        <td class="num">${row.SoBuoi ?? 0}</td>
      </tr>
    `).join('');

    const html = `<!DOCTYPE html>
<html lang="vi">
<head>
  <meta charset="UTF-8" />
  <title>Phiếu coi thi, chấm thi, lâm sàng - ${teacherName}</title>
  <style>
    body { font-family: "Times New Roman", sans-serif; padding: 20px; }
    .center { text-align: center; }
    h1 { font-size: 18px; margin: 4px 0 12px; }
    h2 { font-size: 16px; margin: 6px 0; }
    .header-line { margin: 2px 0; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; }
    th, td { border: 1px solid #333; padding: 6px 8px; font-size: 13px; }
    th { background: #d8f2f0; text-align: center; }
    td { vertical-align: top; }
    .num { text-align: right; }
    .totals td { background: #eee; font-weight: bold; }
    .signature { display: flex; justify-content: space-between; margin-top: 40px; }
    .signature div { width: 32%; text-align: center; }
  </style>
  <script>
    function doPrint(){ setTimeout(() => { window.print(); }, 300); }
    window.onload = doPrint;
  </script>
</head>
<body>
  <div class="left">
    <div class="header-line">TRƯỜNG CAO ĐẲNG Y TẾ THÁI BÌNH</div>
    <div class="header-line">KHOA/ BỘ MÔN: ........................................</div>
    <h1>PHIẾU COI THI, CHẤM THI, LÂM SÀNG</h1>
  </div>
  <div>Họ tên giảng viên: <strong>${teacherName}</strong></div>
  <div>Giảng dạy môn: ........................................</div>
  <div>Tháng/Năm: <strong>${m + 1}/${y}</strong></div>

  <table>
    <thead>
      <tr>
        <th>Ngày thi/Chấm/LS</th>
        <th>Buổi</th>
        <th>Nội dung</th>
        <th>Khóa/Lớp/Nhóm</th>
        <th>Số phút</th>
        <th>Số bài</th>
        <th>Số buổi</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml}
      <tr class="totals">
        <td colspan="4" style="text-align:right">Tổng:</td>
        <td class="num">${totalPhut}</td>
        <td class="num">${totalBai}</td>
        <td class="num">${totalBuoi}</td>
      </tr>
    </tbody>
  </table>

  <div class="signature">
    <div>Phòng QLĐT</div>
    <div>Trưởng khoa/Bộ môn</div>
    <div>Giảng viên</div>
  </div>
</body>
</html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  return (
    <div style={{ padding: 16 }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Nhập giờ coi thi / chấm thi / lâm sàng">
            <Form form={form} layout="vertical">
              <Row gutter={12}>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item label="Ngày" name="Ngay" rules={[{ required: true, message: 'Chọn ngày' }]}>
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item label="Buổi" name="Buoi" rules={[{ required: true, message: 'Chọn buổi' }]}>
                    <Select options={buoiOptions.map(v => ({ label: v, value: v }))} allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Form.Item label="Loại" name="Category">
                    <Select options={categoryOptions.map(v => ({ label: v, value: v }))} allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={6}>
                  <Form.Item label="Khóa/Lớp/Nhóm" name="KhoaLopNhom">
                    <Input placeholder="VD: K46D CNTT / L01 / N2" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item label="Nội dung" name="NoiDung">
                    <Input.TextArea rows={2} placeholder="Mô tả nội dung công việc" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8} md={4}>
                  <Form.Item label="Số phút" name="SoPhut" initialValue={0}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8} md={4}>
                  <Form.Item label="Số bài" name="SoBai" initialValue={0}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8} md={4}>
                  <Form.Item label="Số buổi" name="SoBuoi" initialValue={0}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item label=" " colon={false}>
                    <Space>
                      <Button type="primary" onClick={onSubmit}>{editId ? 'Cập nhật' : 'Lưu'}</Button>
                      {editId && <Button onClick={onCancelEdit}>Hủy sửa</Button>}
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title="Danh sách đã nhập"
            extra={
              <Space>
                <DatePicker value={fromDate} onChange={(d) => setFromDate(d || dayjs().startOf('month'))} format="DD/MM/YYYY" />
                <DatePicker value={toDate} onChange={(d) => setToDate(d || dayjs())} format="DD/MM/YYYY" />
                <Button onClick={load} loading={loading}>Tải</Button>
                <Button onClick={printMonthSheet}>In tháng</Button>
              </Space>
            }
          >
            <Row gutter={12} style={{ marginBottom: 12 }}>
              <Col xs={24} sm={8}><Statistic title="Tổng phút" value={totals.phut} /></Col>
              <Col xs={24} sm={8}><Statistic title="Tổng bài" value={totals.bai} /></Col>
              <Col xs={24} sm={8}><Statistic title="Tổng buổi" value={totals.buoi} /></Col>
            </Row>
            <Table
              size="small"
              rowKey="ID"
              dataSource={items}
              columns={columnsDef(onEdit, onDelete)}
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </Col>

  {/* Removed monthly statistics section as requested */}
      </Row>
    </div>
  );
};

export default TeacherExtraHoursPage;
